import ms from 'ms';
import * as React from 'react';
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

import { modalTypes } from '@/enums';
import useIsWiderThan from '@/hooks/useIsWiderThan';
import { useSubmitSaveSearch } from '@/hooks/useSubmitSaveSearch';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { isUserLoggedIn } from '@/redux/modules/account/user/user.selectors';
import { isE2ETest } from '@/redux/modules/browser';
import { fetchCategoriesIfNeeded } from '@/redux/modules/category/category.actions';
import { getCategory } from '@/redux/modules/category/category.selectors';
import { getJoinModalLastSeenCookie } from '@/redux/modules/cookies';
import { fetchItemSummaryIfNeeded } from '@/redux/modules/item/summary/itemSummary.actions';
import { getItemSummaryTitle } from '@/redux/modules/item/summary/itemSummary.selectors';
import { fetchItemFacetsIfNeeded, getItemFacets } from '@/redux/modules/itemFacets';
import { getPageLabel } from '@/redux/modules/landingPage';
import { openCompleteAccountSignUpModal } from '@/redux/modules/modal';
import { getOpenModal } from '@/redux/modules/modal.selectors';
import { getSaveSearchParams } from '@/redux/modules/saveSearch';
import { getSearchFacets } from '@/redux/modules/searchFacetAggregates.selectors';
import { SearchParams } from '@/types/SaveSearch';
import exitIntent from '@/utils/exitIntent';
import { parseQueryParams } from '@/utils/queryParams';
import { getFinalCategoryIdFromPathname, getIdFromPath, getOnTheHuntItemSearchParams } from '@/utils/urls';

import useOnTheHuntModalContext from '../AuthModal/hooks/useOnTheHuntModalContext';

const sixtySeconds = ms('60s');
const twentySeconds = ms('20s');

const OnTheHuntModal = () => {
    const dispatch = useAppDispatch();
    const submitSaveSearch = useSubmitSaveSearch();
    const { pathname, search } = useLocation();
    const queryParams = parseQueryParams(search);
    const isE2E = useAppSelector(isE2ETest);
    const wide = useIsWiderThan(600);

    const itemId = getIdFromPath(pathname); // only extracts item IDs from URL
    const itemTitle = useAppSelector((state) => getItemSummaryTitle(state, itemId));
    const facets = useAppSelector((state) => getItemFacets(state, itemId));
    const searchFacets = useAppSelector(getSearchFacets);
    const browseBaseIds = useAppSelector((state) => state.browsePath.baseCategoryIds);
    const categoryId = Number(getFinalCategoryIdFromPathname(pathname));
    const category = useAppSelector((state) => getCategory(state, categoryId));
    const categoryPageLabel = useAppSelector(getPageLabel);
    const [exiting, setExiting] = React.useState(false);
    const [savedSearch, setSavedSearch] = React.useState(false);
    const [huntSignUpActive, setHuntSignUpActive] = React.useState(false);
    const openModal = useAppSelector(getOpenModal);
    const isFirstTimeModalOpen = openModal === modalTypes.ON_THE_HUNT;
    const userLoggedIn = useAppSelector(isUserLoggedIn);
    const isWebClient = typeof window !== 'undefined';
    const exitIntentModalSeen = Boolean(isWebClient && window.localStorage.onTheHuntModalSeen);
    const autoPopStarted = Boolean(useAppSelector(getJoinModalLastSeenCookie));
    const onTheHuntModalContext = useOnTheHuntModalContext();
    const timer = React.useRef(null);
    const exitIntentTimeout = React.useRef(null);

    useEffect(() => {
        dispatch(
            fetchItemSummaryIfNeeded({
                identifier: 'on-the-hunt',
                itemId,
            })
        );
        dispatch(fetchItemFacetsIfNeeded([itemId]));
        dispatch(fetchCategoriesIfNeeded({ categoryIds: [categoryId] }));
    }, [categoryId, dispatch, itemId]);

    const title = React.useMemo(() => {
        let title: string;

        if (itemTitle) {
            title = itemTitle;
        } else if (category.name) {
            title = categoryPageLabel;
        }

        return title;
    }, [category.name, categoryPageLabel, itemTitle]);

    const popExitIntentModal = React.useCallback(() => {
        if (!isFirstTimeModalOpen || isE2E) {
            return null;
        }
        setHuntSignUpActive(true);
        dispatch(openCompleteAccountSignUpModal(onTheHuntModalContext));
    }, [dispatch, isE2E, isFirstTimeModalOpen, onTheHuntModalContext]);

    // if they've never seen exit intent modal before, no modals are open, they have exiting intent,
    // they are not logged in and the modal has fired this session, then open it up
    React.useEffect(() => {
        if (exiting && !huntSignUpActive && !userLoggedIn) {
            if (timer.current) {
                clearTimeout(timer.current);
            }
            exitIntentTimeout.current = window.setTimeout(popExitIntentModal, 100);
            window.localStorage.onTheHuntModalSeen = true;
        }
        return () => {
            if (timer.current) {
                clearTimeout(timer.current);
            }
            if (exitIntentTimeout.current) {
                clearTimeout(exitIntentTimeout.current);
            }
        };
    }, [popExitIntentModal, exiting, huntSignUpActive, timer, userLoggedIn]);

    // exit intent timeout: on small screens pop after 20 seconds, 60 on large
    React.useEffect(() => {
        if (autoPopStarted && !huntSignUpActive && !userLoggedIn) {
            const milliSecondsToFire = wide ? sixtySeconds : twentySeconds;
            timer.current = setTimeout(function () {
                setExiting(true);
            }, milliSecondsToFire);
        }
        return () => {
            if (timer.current) {
                clearTimeout(timer.current);
            }
        };
    }, [autoPopStarted, huntSignUpActive, userLoggedIn, wide]);

    // Start listening for exit intent if no modals are open, auto pop sign-up modal has been seen
    // and we're on an item, category or price-guide page
    React.useEffect(() => {
        if (autoPopStarted && !exiting && !userLoggedIn) {
            return exitIntent({
                onExiting: setExiting,
                scrollBackPercent: 5,
                scrollMinimumPercent: 33,
            });
        }
    }, [exiting, userLoggedIn, autoPopStarted, exitIntentModalSeen]);

    // Save search if user logged in when prompted
    React.useEffect(() => {
        if (userLoggedIn && !savedSearch && huntSignUpActive) {
            let saveSearchParams: SearchParams;

            if (itemId) {
                saveSearchParams = getOnTheHuntItemSearchParams(facets, title);
            } else {
                const cleanKeywords = true;
                saveSearchParams = getSaveSearchParams(
                    queryParams,
                    pathname,
                    cleanKeywords,
                    browseBaseIds,
                    searchFacets
                );
            }

            if (saveSearchParams) {
                // this modal can show up on any page, so we don't have any guarantee queryParams will be defined or have status, thus the any cast
                saveSearchParams.status = (queryParams as any)?.status === 'archive' ? 'archive' : 'online';
                submitSaveSearch(saveSearchParams, true);
                setSavedSearch(true);
            }
        }
    }, [
        userLoggedIn,
        categoryId,
        facets,
        itemId,
        pathname,
        queryParams,
        savedSearch,
        title,
        huntSignUpActive,
        browseBaseIds,
        searchFacets,
        submitSaveSearch,
    ]);

    return null;
};

export default OnTheHuntModal;
